import { Controller } from "@hotwired/stimulus"

const UPLOAD_TIMEOUT_MS = 120_000

export default class extends Controller {
  static targets = [
    "input",
    "container",
    "fileList",
    "progressArea",
    "progressBar",
    "overallProgress",
    "settingsPanel",
    "emailPanel",
    "emailAddress",
    "copyIcon",
    "copySuccessIcon",
    "copyFeedback",
    "uploadControls",
    "uploadButton",
    "uploadButtonLabel"
  ]
  static values = {
    url: String
  }

  connect() {
    this.pendingFiles = new Map()
    this.uploadStats = { completed: 0, total: 0, errors: 0 }
    this.uploadInProgress = false
    this.handleUploadSurfaceClose = this.handleUploadSurfaceClose.bind(this)
    window.addEventListener('upload-surface:close', this.handleUploadSurfaceClose)
    this.refreshPendingState()
  }

  disconnect() {
    window.removeEventListener('upload-surface:close', this.handleUploadSurfaceClose)
    if (this.copyResetTimeout) clearTimeout(this.copyResetTimeout)
  }

  async copyEmail(event) {
    event.stopPropagation()

    try {
      await navigator.clipboard.writeText(this.emailAddressTarget.textContent.trim())
    } catch (error) {
      console.error("Unable to copy the upload email address", error)
      this.showCopyFeedback("Nepavyko nukopijuoti")
      return
    }

    this.showCopyFeedback("Nukopijuota", true)
  }

  showCopyFeedback(message, copied = false) {
    this.copyIconTarget.classList.toggle("hidden", copied)
    this.copySuccessIconTarget.classList.toggle("hidden", !copied)
    this.copyFeedbackTarget.textContent = message
    this.copyFeedbackTarget.classList.remove("sr-only")

    if (this.copyResetTimeout) clearTimeout(this.copyResetTimeout)
    this.copyResetTimeout = setTimeout(() => {
      this.copyIconTarget.classList.remove("hidden")
      this.copySuccessIconTarget.classList.add("hidden")
      this.copyFeedbackTarget.textContent = ""
      this.copyFeedbackTarget.classList.add("sr-only")
      this.copyResetTimeout = null
    }, 2000)
  }

  browse() {
    if (this.uploadInProgress) return

    this.inputTarget.click()
  }

  handleBrowse(event) {
    const files = event.target.files
    if (files.length > 0) {
      this.stageFiles(files)
    }
    this.inputTarget.value = ''
  }

  stageFiles(files) {
    if (this.uploadInProgress) return

    Array.from(files).forEach(file => {
      const fileId = this.generateFileId()
      const validation = this.validateFile(file)

      this.addFileToList(file, fileId, validation.error)

      if (validation.valid) {
        this.pendingFiles.set(fileId, file)
      }
    })

    this.refreshPendingState()
  }

  async startUpload() {
    if (this.uploadInProgress) return

    const pendingEntries = Array.from(this.pendingFiles.entries())
    const validFiles = pendingEntries.map(([, file]) => file)
    if (validFiles.length === 0) return

    const manualUploadSettings = this.collectManualUploadSettings()
    this.uploadInProgress = true
    this.uploadStats.total = validFiles.length
    this.uploadStats.completed = 0
    this.uploadStats.errors = 0
    this.disableUploadControls()
    this.showProgressArea()

    pendingEntries.forEach(([fileId, file]) => {
      this.uploadFile(file, fileId, manualUploadSettings)
    })
  }

  cancelPending(event) {
    event?.preventDefault()
    if (this.uploadInProgress) return

    this.resetPendingUpload()
  }

  removePendingFile(event) {
    event.preventDefault()
    if (this.uploadInProgress) return

    const fileId = event.currentTarget.dataset.fileId
    this.pendingFiles.delete(fileId)
    document.getElementById(fileId)?.remove()
    this.refreshPendingState()
  }

  validateFile(file) {
    // Check file type - PDFs and images allowed
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png'
    ]

    const allowedExtensions = ['.pdf', '.jpeg', '.jpg', '.png']
    const fileExtension = file.name.toLowerCase().slice(file.name.lastIndexOf('.'))

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      return {
        valid: false,
        error: 'Palaikomi PDF failai ir nuotraukos (JPG, PNG)'
      }
    }

    // Check file size - 10MB max
    const maxSize = 10 * 1024 * 1024 // 10MB
    if (file.size > maxSize) {
      return {
        valid: false,
        error: 'Failo dydis negali viršyti 10MB'
      }
    }

    // Check if file is empty
    if (file.size === 0) {
      return {
        valid: false,
        error: 'Failas negali būti tuščias'
      }
    }

    return { valid: true }
  }

  async uploadFile(file, fileId, manualUploadSettings = null) {
    const formData = new FormData()
    formData.append('files[]', file)
    if (manualUploadSettings) {
      formData.append('manual_upload_settings', JSON.stringify(manualUploadSettings))
    }
    this.updateFileStatus(fileId, 'uploading')

    try {
      const csrfToken = document.querySelector('[name="csrf-token"]')?.content
      const headers = { 'Accept': 'application/json' }
      if (csrfToken) headers['X-CSRF-Token'] = csrfToken

      const response = await fetch(this.urlValue, {
        method: 'POST',
        body: formData,
        headers,
        signal: AbortSignal.timeout(UPLOAD_TIMEOUT_MS)
      })

      const data = await response.json()

      if (response.ok) {
        this.renderUploadStream(data)
        this.updateFileStatus(fileId, 'success')
        this.uploadStats.completed++
      } else {
        // Parse error message from server response
        let errorMessage = 'Nepavyko įkelti failo'
        let errorAction = null
        if (data && data.error) {
          errorMessage = data.error
        }

        // Handle billing support responses (status 402)
        if (response.status === 402 && data.redirect_path) {
          errorAction = this.billingErrorAction(data.redirect_path)
        }

        // Handle verification required error (status 403)
        if (response.status === 403 && data.verification_required) {
          errorMessage = data.error || 'Prašome patvirtinti savo el. pašto adresą prieš tęsiant'
        }

        this.updateFileStatus(fileId, 'error', errorMessage, errorAction)
        this.uploadStats.errors++
      }
    } catch (error) {
      const message = error.name === 'TimeoutError'
        ? 'Įkėlimas užtruko per ilgai. Patikrinkite interneto ryšį ir bandykite dar kartą.'
        : 'Ryšio klaida. Patikrinkite interneto ryšį.'
      this.updateFileStatus(fileId, 'error', message)
      this.uploadStats.errors++
    }

    this.pendingFiles.delete(fileId)
    this.updateProgress()
  }

  renderUploadStream(data) {
    if (!data.turbo_stream || !this.canInsertUploadedRows() || !window.Turbo) return

    window.Turbo.renderStreamMessage(data.turbo_stream)
  }

  canInsertUploadedRows() {
    const params = new URLSearchParams(window.location.search)
    return !params.has('state') && !params.has('filename')
  }

  addFileToList(file, fileId, errorMessage = null) {
    const truncatedName = this.truncateFilename(file.name, 30)
    const statusText = errorMessage ? 'Klaida' : 'Paruošta įkelti'
    const statusClass = errorMessage ? 'text-red-500' : 'text-gray-600'
    const escapedName = this.escapeHTML(file.name)
    const escapedTruncatedName = this.escapeHTML(truncatedName)
    const escapedErrorMessage = errorMessage ? this.escapeHTML(errorMessage) : null
    const removeButton = errorMessage ? '' : `
      <button type="button"
              data-action="click->dropzone#removePendingFile"
              data-file-id="${fileId}"
              class="pending-remove inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900"
              aria-label="Pašalinti failą"
              title="Pašalinti failą">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.8">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
      </button>
    `

    const html = `
      <div id="${fileId}" class="flex flex-col p-2 bg-gray-50 rounded" data-pending-valid="${!errorMessage}" data-file-size="${file.size}">
        <div class="flex items-center justify-between">
          <div class="flex items-center min-w-0 flex-1">
            <span class="text-sm truncate" title="${escapedName}">${escapedTruncatedName}</span>
            <span class="text-xs text-gray-500 ml-2 shrink-0">${this.formatFileSize(file.size)}</span>
          </div>
          <div class="flex items-center gap-2 ml-2 shrink-0">
            <span class="status text-sm ${statusClass}">${statusText}</span>
            <svg class="spinner hidden h-4 w-4 text-gray-600 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            ${removeButton}
          </div>
        </div>
        ${escapedErrorMessage ? `<div class="error-message text-xs text-red-600 mt-1">${escapedErrorMessage}</div>` : '<div class="error-message text-xs text-red-600 mt-1 hidden"></div>'}
        <div class="success-message text-xs text-green-700 mt-1 hidden">
          Dokumentas įkeltas. Kai būsena taps „Apdorota“, dokumento eilutėje spauskite „Atidaryti sąskaitą“.
        </div>
      </div>
    `
    this.fileListTarget.insertAdjacentHTML('beforeend', html)
  }

  truncateFilename(filename, maxLength) {
    if (filename.length <= maxLength) return filename

    const extension = filename.split('.').pop()
    const nameWithoutExt = filename.slice(0, -(extension.length + 1))

    const truncatedLength = maxLength - 3 - extension.length
    return `${nameWithoutExt.slice(0, truncatedLength)}...${extension}`
  }

  updateFileStatus(fileId, status, errorMessage = null, errorAction = null) {
    const fileEl = document.getElementById(fileId)

    if (!fileEl) return

    const statusEl = fileEl.querySelector('.status')
    const spinnerEl = fileEl.querySelector('.spinner')
    const errorMessageEl = fileEl.querySelector('.error-message')
    const successMessageEl = fileEl.querySelector('.success-message')

    switch (status) {
      case 'uploading':
        statusEl.textContent = 'Įkeliama...'
        statusEl.className = 'status text-sm text-gray-600'
        spinnerEl.classList.remove('hidden')
        fileEl.querySelector('.pending-remove')?.remove()
        errorMessageEl.classList.add('hidden')
        successMessageEl.classList.add('hidden')
        break
      case 'success':
        statusEl.textContent = 'Įkelta, apdorojama'
        statusEl.className = 'status text-sm text-green-500'
        spinnerEl.classList.add('hidden')
        errorMessageEl.classList.add('hidden')
        successMessageEl.classList.remove('hidden')
        break
      case 'error':
        statusEl.textContent = 'Klaida'
        statusEl.className = 'status text-sm text-red-500'
        spinnerEl.classList.add('hidden')
        successMessageEl.classList.add('hidden')
        if (errorMessage && errorMessageEl) {
          errorMessageEl.replaceChildren(document.createTextNode(String(errorMessage)))

          if (errorAction) {
            const link = document.createElement('a')
            link.href = errorAction.href
            link.className = 'underline text-blue-600 hover:text-blue-700'
            link.textContent = errorAction.label
            errorMessageEl.append(document.createTextNode(' '), link)
          }

          errorMessageEl.classList.remove('hidden')
        }
        break
    }
  }

  billingErrorAction(redirectPath) {
    if (typeof redirectPath !== 'string') return null

    let url
    try {
      url = new URL(redirectPath, window.location.origin)
    } catch (_error) {
      return null
    }

    if (url.origin !== window.location.origin || url.hash) return null

    const queryKeys = Array.from(url.searchParams.keys())
    const billingOverview = url.pathname === '/settings' &&
      url.searchParams.get('section') === 'billing' &&
      url.searchParams.getAll('section').length === 1 &&
      queryKeys.length === 1 &&
      queryKeys.every(key => key === 'section')

    if (billingOverview) {
      return { href: `${url.pathname}${url.search}`, label: 'Apmokėjimo informacija' }
    }

    const billingHistory = url.pathname === '/settings' &&
      url.searchParams.get('section') === 'billing' &&
      url.searchParams.get('subsection') === 'billing_history' &&
      url.searchParams.getAll('section').length === 1 &&
      url.searchParams.getAll('subsection').length === 1 &&
      queryKeys.length === 2 &&
      queryKeys.every(key => ['section', 'subsection'].includes(key))

    if (billingHistory) {
      return { href: `${url.pathname}${url.search}`, label: 'Peržiūrėti sąskaitas' }
    }

    return null
  }

  escapeHTML(value) {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
  }

  generateFileId() {
    return `file-${crypto.randomUUID()}`
  }

  formatFileSize(bytes) {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`
  }

  collectManualUploadSettings() {
    const fields = this.element.querySelectorAll('[data-manual-upload-setting]')
    if (fields.length === 0) return null

    const settings = { schema_version: 1 }
    fields.forEach(field => {
      const value = field.value
      if (!value) return

      this.assignNestedValue(settings, field.dataset.manualUploadSetting, value)

      const copyPath = field.dataset.manualUploadSettingCopyTo
      if (copyPath) {
        this.assignNestedValue(settings, copyPath, value)
      }
    })

    return settings
  }

  assignNestedValue(target, path, value) {
    const keys = path.split('.')
    const lastKey = keys.pop()
    const parent = keys.reduce((current, key) => {
      current[key] ||= {}
      return current[key]
    }, target)

    parent[lastKey] = Number.parseInt(value, 10)
  }

  preventDefault(e) {
    e.preventDefault()
  }

  dragenter(e) {
    e.preventDefault()
    this.containerTarget.classList.add('border-gray-500')
  }

  dragover(e) {
    e.preventDefault()
  }

  dragleave(e) {
    e.preventDefault()
    this.containerTarget.classList.remove('border-gray-500')
  }

  drop(e) {
    e.preventDefault()
    this.containerTarget.classList.remove('border-gray-500')
    const files = e.dataTransfer.files
    this.stageFiles(files)
  }

  showProgressArea() {
    if (this.hasProgressAreaTarget) {
      this.progressAreaTarget.classList.remove('hidden')
    }
  }

  updateProgress() {
    const totalProcessed = this.uploadStats.completed + this.uploadStats.errors
    const progress = Math.round((totalProcessed / this.uploadStats.total) * 100)

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${progress}%`
    }

    if (this.hasOverallProgressTarget) {
      this.overallProgressTarget.textContent = `${progress}%`
    }

    // Hide progress when all files are processed
    if (totalProcessed === this.uploadStats.total) {
      this.finishUploadBatch()

      if (this.hasProgressAreaTarget) {
        setTimeout(() => {
          this.progressAreaTarget.classList.add('hidden')
        }, 2000)
      }
    }
  }

  refreshPendingState() {
    const hasValidPendingFiles = this.pendingFiles.size > 0

    this.updatePendingFilePresentation()

    if (this.hasSettingsPanelTarget) {
      this.settingsPanelTarget.classList.toggle('hidden', !hasValidPendingFiles)
    }

    if (this.hasUploadControlsTarget) {
      this.uploadControlsTarget.classList.toggle('hidden', !hasValidPendingFiles)
    }

    if (this.hasEmailPanelTarget) {
      this.emailPanelTarget.classList.toggle('hidden', hasValidPendingFiles)
    }

    if (this.hasContainerTarget) {
      this.containerTarget.classList.toggle('hidden', hasValidPendingFiles)
    }

    this.updateUploadButtonLabel()
  }

  updatePendingFilePresentation() {
    if (!this.hasFileListTarget) return

    this.fileListTarget.querySelector('.pending-upload-summary')?.remove()

    const validRows = this.validFileRows()
    if (validRows.length <= 1) {
      validRows.forEach(row => row.classList.remove('hidden'))
      return
    }

    const totalBytes = validRows.reduce((sum, row) => (
      sum + Number.parseInt(row.dataset.fileSize || '0', 10)
    ), 0)
    const invalidCount = this.invalidFileRows().length
    const title = this.pendingUploadSummaryTitle(validRows.length)
    const invalidHint = invalidCount > 0
      ? ` · <span class="text-red-600">${invalidCount} su klaida rodoma žemiau</span>`
      : ''

    this.fileListTarget.insertAdjacentHTML('afterbegin', `
      <div class="pending-upload-summary rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
        <div class="min-w-0">
          <div class="flex flex-wrap items-center gap-2">
            <span class="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-white px-2 text-xs font-semibold text-gray-700 ring-1 ring-gray-200">${validRows.length}</span>
            <p class="text-sm font-semibold text-gray-900">${title}</p>
          </div>
          <p class="mt-1 text-sm text-gray-600">
            ${this.formatFileSize(totalBytes)}${invalidHint}
          </p>
        </div>
      </div>
    `)

    validRows.forEach(row => {
      row.classList.add('hidden')
    })
  }

  pendingUploadSummaryTitle(count) {
    if (this.uploadInProgress) {
      return `Įkeliami failai: ${count}`
    }

    if (this.pendingFiles.size > 0) {
      return `Pasirinkti failai: ${count}`
    }

    return `Įkelti failai: ${count}`
  }

  validFileRows() {
    if (!this.hasFileListTarget) return []

    return Array.from(this.fileListTarget.querySelectorAll('[data-pending-valid="true"]'))
  }

  invalidFileRows() {
    if (!this.hasFileListTarget) return []

    return Array.from(this.fileListTarget.querySelectorAll('[data-pending-valid="false"]'))
  }

  updateUploadButtonLabel() {
    if (!this.hasUploadButtonLabelTarget) return

    const count = this.pendingFiles.size
    this.uploadButtonLabelTarget.textContent = count === 1 ? 'Įkelti dokumentą' : `Įkelti ${count} dokumentus`
  }

  disableUploadControls() {
    if (this.hasUploadButtonTarget) {
      this.uploadButtonTarget.disabled = true
    }

    this.element.querySelectorAll('[data-manual-upload-setting], [data-action*="lookup-selector"], button').forEach(control => {
      if (control.closest('[data-dropzone-target="fileList"]')) return
      control.disabled = true
    })
  }

  enableUploadControls() {
    if (this.hasUploadButtonTarget) {
      this.uploadButtonTarget.disabled = false
    }

    this.element.querySelectorAll('[data-manual-upload-setting], [data-action*="lookup-selector"], button').forEach(control => {
      control.disabled = false
    })
  }

  finishUploadBatch() {
    this.uploadInProgress = false
    this.enableUploadControls()
    this.refreshPendingState()
  }

  resetPendingUpload() {
    this.pendingFiles.clear()
    this.fileListTarget.replaceChildren()
    this.uploadStats = { completed: 0, total: 0, errors: 0 }

    if (this.hasProgressAreaTarget) {
      this.progressAreaTarget.classList.add('hidden')
    }

    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = '0%'
    }

    if (this.hasOverallProgressTarget) {
      this.overallProgressTarget.textContent = '0%'
    }

    this.refreshPendingState()
  }

  handleUploadSurfaceClose() {
    if (this.uploadInProgress) return

    this.resetPendingUpload()
  }

}
